import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getTokenBalance, approveToken } from "@/helper/contractFunctions";
import config from "@/config/config";
import {
  endBoozeBot, endBulletBot, endBustOutBot, endCarBot, endCrimeBot, endKSBot,
  endNarcsBot, endRaceXPBot, startBoozeBot, startBulletBot, startBustOutBot,
  startCarBot, startCrimeBot, startKSBot, startNarcsBot, startRaceXPBot,
  getUserHelperBotInfoBatch,
} from "@/helper/contractFunctions/MafiaHelperBot";
import {
  approveHelperCredit,
  balanceOfHelperCredit,
  buyCredit,
  getHelperCreditSwapTokens,
  getHelperPrice,
} from "@/helper/contractFunctions/MafiaHelperCredit";
import { SwapToken } from "@/types/Contract/SwapToken";
import { BulletBotPlusInfo } from "@/types/Contract/HelperBot/BulletBotPlusInfo";
import { HelperBotInfo } from "@/types/Contract/HelperBot/HelperBotInfo";
import { UserBotPerkInfo } from "@/types/Contract/HelperBot/UserBotPerkInfo";

const defaultBot: HelperBotInfo = { successRate: 0, startTimestamp: 0, endTimestamp: 0, attemptCount: 0, isRunning: false };
const defaultBulletPlus: BulletBotPlusInfo = { amountModifiedPercent: 0, priceModifiedPercent: 0 };
const defaultPerk: UserBotPerkInfo = { successRatePerk: 0, cooldownReductionPerk: 0, cashRewardPerk: 0, creditCostReductionPerk: 0, bulletDoublePerk: false, perkDuration: 0 };

export interface HelperBotState {
  isLoadingHelperBotInfo: boolean;
  userCrimeBotInfo: HelperBotInfo; userCarBotInfo: HelperBotInfo; userKSBotInfo: HelperBotInfo;
  userBoozeBotInfo: HelperBotInfo; userNarcsBotInfo: HelperBotInfo; userBulletBotInfo: HelperBotInfo;
  userBulletBotPlusInfo: BulletBotPlusInfo; userRaceXPBotInfo: HelperBotInfo; userBustOutXPBotInfo: HelperBotInfo;
  crimeBotPerkInfo: UserBotPerkInfo; carBotPerkInfo: UserBotPerkInfo; boozeBotPerkInfo: UserBotPerkInfo;
  narcsBotPerkInfo: UserBotPerkInfo; bulletBotPerkInfo: UserBotPerkInfo; killSkillBotPerkInfo: UserBotPerkInfo;
  raceXPBotPerkInfo: UserBotPerkInfo; bustOutXPBotPerkInfo: UserBotPerkInfo;
  isApprovingHelperCredit: boolean; isStartingHelperBot: boolean; isEndingHelperBot: boolean;
  helperCreditBalance: number;
  helperCreditSwapTokenInfo: SwapToken[];
  helperCreditSwapTokenBalances: Record<string, number>;
  helperCreditPrice: number;
  isBuyingHelperCredit: boolean;
}
const initialState: HelperBotState = {
  isLoadingHelperBotInfo: false,
  userCrimeBotInfo: defaultBot, userCarBotInfo: defaultBot, userKSBotInfo: defaultBot,
  userBoozeBotInfo: defaultBot, userNarcsBotInfo: defaultBot, userBulletBotInfo: defaultBot,
  userBulletBotPlusInfo: defaultBulletPlus, userRaceXPBotInfo: defaultBot, userBustOutXPBotInfo: defaultBot,
  crimeBotPerkInfo: defaultPerk, carBotPerkInfo: defaultPerk, boozeBotPerkInfo: defaultPerk,
  narcsBotPerkInfo: defaultPerk, bulletBotPerkInfo: defaultPerk, killSkillBotPerkInfo: defaultPerk,
  raceXPBotPerkInfo: defaultPerk, bustOutXPBotPerkInfo: defaultPerk,
  isApprovingHelperCredit: false, isStartingHelperBot: false, isEndingHelperBot: false,
  helperCreditBalance: 0, helperCreditSwapTokenInfo: [], helperCreditSwapTokenBalances: {},
  helperCreditPrice: 0, isBuyingHelperCredit: false,
};
export const getUserHelperBotInfo = createAsyncThunk(
  "helperBot/getUserHelperBotInfo",
  async (account: string) => getUserHelperBotInfoBatch(account)
);

export const startCrimeBotAction = createAsyncThunk(
  "helperBot/startCrimeBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startCrimeBot(account, attemptCount, perkItemIds);

    return data.transactionHash;
  }
);

export const endCrimeBotAction = createAsyncThunk(
  "helperBot/endCrimeBotAction",
  async (account: string) => {
    const data = await endCrimeBot(account);
    return data.transactionHash;
  }
);

export const startCarBotAction = createAsyncThunk(
  "helperBot/startCarBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startCarBot(account, attemptCount, perkItemIds);
    return data.transactionHash;
  }
);

export const endCarBotAction = createAsyncThunk(
  "helperBot/endCarBotAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endCarBot(account, signMsg, signature);
    return data.transactionHash;
  }
);

export const startKSBotAction = createAsyncThunk(
  "helperBot/startKSBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startKSBot(account, attemptCount, perkItemIds);
    return data.transactionHash;
  }
);

export const endKSBotAction = createAsyncThunk(
  "helperBot/endKSBotAction",
  async (account: string) => {
    const data = await endKSBot(account);
    return data.transactionHash;
  }
);

export const startBoozeBotAction = createAsyncThunk(
  "helperBot/startBoozeBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startBoozeBot(account, attemptCount, perkItemIds);
    return data.transactionHash;
  }
);

export const endBoozeBotAction = createAsyncThunk(
  "helperBot/endBoozeBotAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endBoozeBot(account, signMsg, signature);
    return data.transactionHash;
  }
);

export const startBulletDealerBotAction = createAsyncThunk(
  "helperBot/startBulletDealerBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startBulletBot(account, attemptCount, perkItemIds);
    return data.transactionHash;
  }
);

export const endBulletDealerBotAction = createAsyncThunk(
  "helperBot/endBulletDealerBotAction",
  async ({
    cashAmount,
    account,
    signMsg,
    signature,
  }: {
    cashAmount: string;
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endBulletBot(cashAmount, account, signMsg, signature);
    return data.transactionHash;
  }
);

export const startRaceXPBotAction = createAsyncThunk(
  "helperBot/startRaceXPBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startRaceXPBot(account, attemptCount, perkItemIds);
    return data.transactionHash;
  }
);

export const endRaceXPBotAction = createAsyncThunk(
  "helperBot/endRaceXPBotAction",
  async (account: string) => {
    const data = await endRaceXPBot(account);
    return data.transactionHash;
  }
);

export const startBustOutXPBotAction = createAsyncThunk(
  "helperBot/startBustOutXPBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startBustOutBot(account, attemptCount, perkItemIds);
    return data.transactionHash;
  }
);

export const endBustOutXPBotAction = createAsyncThunk(
  "helperBot/endBustOutXPBotAction",
  async (account: string) => {
    const data = await endBustOutBot(account);
    return data.transactionHash;
  }
);

export const startNarcsBotAction = createAsyncThunk(
  "helperBot/startNarcsBotAction",
  async ({
    account,
    attemptCount,
    perkItemIds,
  }: {
    account: string;
    attemptCount: number;
    perkItemIds: number[];
  }) => {
    const data = await startNarcsBot(account, attemptCount, perkItemIds);
    return data.transactionHash;
  }
);

export const endNarcsBotAction = createAsyncThunk(
  "helperBot/endNarcsBotAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const data = await endNarcsBot(account, signMsg, signature);
    return data.transactionHash;
  }
);

export const getHelperCreditBalance = createAsyncThunk(
  "helperBot/getHelperCreditBalance",
  async ({ account, signMsg, signature }: { account: string; signMsg: string; signature: string }) =>
    Number(await balanceOfHelperCredit(account, signMsg, signature))
);

export const getHelperCreditSwapTokenInfo = createAsyncThunk(
  "helperBot/getHelperCreditSwapTokens",
  async () => getHelperCreditSwapTokens()
);

export const getHelperCreditSwapTokenBalance = createAsyncThunk(
  "helperBot/getHelperCreditSwapTokenBalance",
  async ({ account, swapTokenInfo }: { account: string; swapTokenInfo: SwapToken[] }) => {
    const balances = await Promise.all(
      swapTokenInfo.map(async (token) => ({
        [token.name]: Number(await getTokenBalance(token.tokenAddress, account)),
      }))
    );
    return Object.assign({}, ...balances) as Record<string, number>;
  }
);

export const getHelperCreditPrice = createAsyncThunk(
  "helperBot/getHelperCreditPrice",
  async () => getHelperPrice()
);

export const buyHelperCreditAction = createAsyncThunk(
  "helperBot/buyHelperCreditAction",
  async ({ account, tokenInfo, amount }: { account: string; tokenInfo: SwapToken; amount: number }) =>
    buyCredit(account, tokenInfo, amount)
);

export const approveHelperCreditPurchaseAction = createAsyncThunk(
  "helperBot/approveHelperCreditPurchaseAction",
  async ({ account, amount, tokenInfo }: { account: string; amount: number; tokenInfo: SwapToken }) => {
    await approveToken(
      tokenInfo.tokenAddress,
      Math.ceil(amount * 1.1),
      config.helperCreditAddress,
      account
    );
  }
);

export const approveHelperCreditAction = createAsyncThunk(
  "helperBot/approveHelperCreditAction",
  async ({
    account,
    spender,
    creditAmount,
  }: {
    account: string;
    spender: string;
    creditAmount: number;
  }) => {
    const data = await approveHelperCredit(creditAmount, spender, account);
    return data.transactionHash;
  }
);

const helperBotSlice = createSlice({
  name: "helperBot", initialState, reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserHelperBotInfo.pending, (state) => { state.isLoadingHelperBotInfo = true; })
      .addCase(getUserHelperBotInfo.fulfilled, (state, { payload }) => {
        state.userCrimeBotInfo = payload.userCrimeBotInfo; state.userCarBotInfo = payload.userCarBotInfo;
        state.userKSBotInfo = payload.userKSBotInfo; state.userNarcsBotInfo = payload.userNarcsBotInfo;
        state.userBoozeBotInfo = payload.userBoozeBotInfo; state.userBulletBotInfo = payload.userBulletBotInfo;
        state.userBulletBotPlusInfo = payload.userBulletBotPlusInfo; state.userRaceXPBotInfo = payload.userRaceXPBotInfo;
        state.userBustOutXPBotInfo = payload.userBustOutXPBotInfo; state.crimeBotPerkInfo = payload.crimeBotPerkInfo;
        state.carBotPerkInfo = payload.carBotPerkInfo; state.boozeBotPerkInfo = payload.boozeBotPerkInfo;
        state.narcsBotPerkInfo = payload.narcsBotPerkInfo; state.bulletBotPerkInfo = payload.bulletBotPerkInfo;
        state.killSkillBotPerkInfo = payload.killSkillBotPerkInfo; state.raceXPBotPerkInfo = payload.raceXPBotPerkInfo;
        state.bustOutXPBotPerkInfo = payload.bustOutXPBotPerkInfo; state.isLoadingHelperBotInfo = false;
      })
      .addCase(getUserHelperBotInfo.rejected, (state) => { state.isLoadingHelperBotInfo = false; });
    const starts = [startCrimeBotAction, startCarBotAction, startKSBotAction, startRaceXPBotAction, startBustOutXPBotAction, startNarcsBotAction, startBulletDealerBotAction] as const;
    starts.forEach((action) => { builder.addCase(action.pending, (state) => { state.isStartingHelperBot = true; }).addCase(action.fulfilled, (state) => { state.isStartingHelperBot = false; }).addCase(action.rejected, (state) => { state.isStartingHelperBot = false; }); });
    builder.addCase(startBoozeBotAction.pending, (state) => { state.isStartingHelperBot = true; }).addCase(startBoozeBotAction.fulfilled, (state) => { state.isStartingHelperBot = false; }).addCase(startBoozeBotAction.rejected, (state, error) => { state.isStartingHelperBot = false; console.log(error); });
    const ends = [endCrimeBotAction, endCarBotAction, endKSBotAction, endRaceXPBotAction, endBustOutXPBotAction, endBoozeBotAction, endNarcsBotAction, endBulletDealerBotAction] as const;
    ends.forEach((action) => { builder.addCase(action.pending, (state) => { state.isEndingHelperBot = true; }).addCase(action.fulfilled, (state) => { state.isEndingHelperBot = false; }).addCase(action.rejected, (state) => { state.isEndingHelperBot = false; }); });
    builder.addCase(approveHelperCreditAction.pending, (state) => { state.isApprovingHelperCredit = true; }).addCase(approveHelperCreditAction.fulfilled, (state) => { state.isApprovingHelperCredit = false; }).addCase(approveHelperCreditAction.rejected, (state) => { state.isApprovingHelperCredit = false; });
    builder
      .addCase(getHelperCreditBalance.fulfilled, (state, { payload }) => { state.helperCreditBalance = payload; })
      .addCase(getHelperCreditSwapTokenInfo.fulfilled, (state, { payload }) => { state.helperCreditSwapTokenInfo = payload; })
      .addCase(getHelperCreditSwapTokenBalance.fulfilled, (state, { payload }) => { state.helperCreditSwapTokenBalances = payload; })
      .addCase(getHelperCreditPrice.fulfilled, (state, { payload }) => { state.helperCreditPrice = payload; })
      .addCase(buyHelperCreditAction.pending, (state) => { state.isBuyingHelperCredit = true; })
      .addCase(buyHelperCreditAction.fulfilled, (state) => { state.isBuyingHelperCredit = false; })
      .addCase(buyHelperCreditAction.rejected, (state) => { state.isBuyingHelperCredit = false; })
      .addCase(approveHelperCreditPurchaseAction.pending, (state) => { state.isBuyingHelperCredit = true; })
      .addCase(approveHelperCreditPurchaseAction.fulfilled, (state) => { state.isBuyingHelperCredit = false; })
      .addCase(approveHelperCreditPurchaseAction.rejected, (state) => { state.isBuyingHelperCredit = false; });
  },
});
export default helperBotSlice.reducer;
